<script setup lang="ts">
/**
 * The team message board: one thread for everyone, and one of the views the
 * switcher offers.
 *
 * It used to be a panel floating over the board, which meant it had its own
 * close button and its own idea of whether it was open. As a view it is simply
 * somewhere you are, like the calendar.
 *
 * Reading is tracked on the server, and only counts while you could actually
 * see the newest message — this view up, the tab visible, and the list scrolled
 * to the bottom. Opening it and scrolling up to read history does not silently
 * clear a badge for messages you have not reached.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { toast } from "../lib/toast.js";
import { MESSAGE_MAX } from "@shared/types.js";

const board = useBoard();

const list = ref<HTMLElement | null>(null);
const input = ref<HTMLTextAreaElement | null>(null);
const draft = ref("");
const sending = ref(false);
const loadingOlder = ref(false);

/** Close enough to the bottom to count as reading the latest, and to follow new messages. */
const NEAR_BOTTOM_PX = 60;

function atBottom(): boolean {
  const el = list.value;
  return !el || el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
}

function scrollToBottom(): void {
  if (list.value) list.value.scrollTop = list.value.scrollHeight;
}

function maybeMarkRead(): void {
  if (document.visibilityState === "visible" && atBottom()) void board.markMessagesRead();
}

const when = (ms: number): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(ms);

/** A run of messages from one person within a few minutes reads as one post:
 *  the name and avatar once, not on every line. */
const GROUP_MS = 5 * 60 * 1000;
function startsGroup(index: number): boolean {
  const m = board.messages[index]!;
  const prev = board.messages[index - 1];
  return !prev || prev.userId !== m.userId || m.createdAt - prev.createdAt > GROUP_MS;
}

// New message: follow it down if the reader was already at the bottom, or it
// is their own. Someone scrolled up reading history is left where they are.
watch(
  () => board.messages.length,
  async () => {
    // An older page landing at the top is loadOlder's to position.
    if (loadingOlder.value) return;
    // Runs before the DOM updates, so this measures where the reader was.
    const follow = atBottom() || board.messages.at(-1)?.userId === board.me?.id;
    await nextTick();
    if (follow) scrollToBottom();
    maybeMarkRead();
  },
);

async function loadOlder(): Promise<void> {
  const el = list.value;
  if (!el || loadingOlder.value) return;
  loadingOlder.value = true;
  // Keep the message under the reader's eye where it is as a page lands above it.
  const fromBottom = el.scrollHeight - el.scrollTop;
  try {
    await board.loadOlderMessages();
    await nextTick();
    el.scrollTop = el.scrollHeight - fromBottom;
  } catch (err) {
    toast(`Could not load earlier messages: ${(err as Error).message}`);
  } finally {
    loadingOlder.value = false;
  }
}

async function send(): Promise<void> {
  const body = draft.value.trim();
  if (!body || sending.value) return;
  sending.value = true;
  try {
    await board.postMessage(body);
    draft.value = "";
  } catch (err) {
    // Keep the draft: a failed send must not cost someone what they typed.
    toast(`Message not sent: ${(err as Error).message}`);
  } finally {
    sending.value = false;
    input.value?.focus();
  }
}

/** Enter sends and Shift+Enter is a new line, as in every chat box. Not while an
 *  IME is composing, where Enter picks the candidate rather than finishing. */
function onKeydown(ev: KeyboardEvent): void {
  if (ev.key === "Enter" && !ev.shiftKey && !ev.isComposing) {
    ev.preventDefault();
    void send();
  }
}

const onVisible = (): void => maybeMarkRead();

onMounted(async () => {
  document.addEventListener("visibilitychange", onVisible);
  if (!board.messagesLoaded) await board.loadMessages().catch(() => undefined);
  await nextTick();
  scrollToBottom();
  maybeMarkRead();
  input.value?.focus();
});

onBeforeUnmount(() => document.removeEventListener("visibilitychange", onVisible));
</script>

<template>
  <section class="messages" aria-label="Team messages">
    <div ref="list" class="messages-list" @scroll.passive="maybeMarkRead">
      <button
        v-if="board.olderMessages"
        class="btn ghost messages-older"
        type="button"
        :disabled="loadingOlder"
        @click="loadOlder"
      >{{ loadingOlder ? "Loading…" : "Load earlier messages" }}</button>

      <p v-if="board.messagesLoaded && !board.messages.length" class="messages-empty">
        No messages yet. Say something to the team.
      </p>

      <div
        v-for="(m, i) in board.messages"
        :key="m.id"
        class="entry message"
        :class="{ continued: !startsGroup(i), mine: m.userId === board.me?.id }"
      >
        <span v-if="startsGroup(i)" class="avatar" :style="{ background: board.userColor(m.userId) }">
          {{ board.userName(m.userId).slice(0, 2) }}
        </span>
        <span v-else class="avatar-spacer"></span>
        <div class="entry-body">
          <div v-if="startsGroup(i)" class="entry-meta">{{ board.userName(m.userId) }} · {{ when(m.createdAt) }}</div>
          <div class="entry-text" :title="when(m.createdAt)">{{ m.body }}</div>
        </div>
      </div>
    </div>

    <div class="composer">
      <textarea
        ref="input"
        v-model="draft"
        rows="1"
        :maxlength="MESSAGE_MAX"
        placeholder="Message the team… (Enter to send)"
        @keydown="onKeydown"
      ></textarea>
      <button class="btn primary" :disabled="!draft.trim() || sending" @click="send">Send</button>
    </div>
  </section>
</template>
